import { CameraAddRegular, DeleteRegular } from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { ButtonWithTooltip } from "components/ButtonWithTooltip/ButtonWithTooltip";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useAppTheme } from "hooks/useAppTheme";
import { useGetImageViewOptions } from "hooks/useGetImageViewOptions";
import { useAtom } from "jotai";
import { extractFileNameFromUrl } from "library/utils";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDeleteImage } from "state/queries/useDeleteImage";
import { selectImagesViewState } from "store/UIAdminPage";
import styled from "styled-components";
import { ViewByType } from "./ViewByType";
import { EImageView } from "library/types";

type Props = {
  onSelectImage?: (event: any, image?: string) => void;
  setOpen: (open: boolean) => void;
  image: string;
};

export function ImageItem({ image, onSelectImage, setOpen }: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const imageViewOptions = useGetImageViewOptions();
  const [viewValue, setViewValue] = useAtom(selectImagesViewState);

  const fileName = extractFileNameFromUrl(image);

  const [deleteImageOpen, setDeleteImageOpen] = useState(false);

  const toggleDeleteImageOpenState = useCallback(() => {
    setDeleteImageOpen((prev) => !prev);
  }, []);

  const { mutate: onDeleteImage, isLoading: deleteInProgress } =
    useDeleteImage();

  const imageViewValue = viewValue || imageViewOptions[0];

  return (
    <>
      <Container>
        {deleteInProgress && <AppSpinner />}

        <ViewByType image={image} type={imageViewValue?.value as EImageView} />
        {onSelectImage ? (
          <ButtonWithTooltip
            data-help="admin-images-select"
            icon={<CameraAddRegular />}
            onClick={(e) => {
              onSelectImage(e, image);
              setOpen(false);
            }}
            tooltipContent={t("hbh.selectImage.label")}
          />
        ) : (
          <ButtonWithTooltip
            data-help="admin-images-delete"
            onClick={() => {
              setDeleteImageOpen(true);
            }}
            disabled={deleteInProgress}
            tooltipContent={t("hbh.deleteImage.label")}
            icon={
              <DeleteRegular
                style={{
                  ...(deleteInProgress
                    ? {}
                    : {
                        color: theme.palette.redDark,
                      }),
                }}
              />
            }
          />
        )}
      </Container>
      {deleteImageOpen && (
        <ConfirmationDialog
          isOpen={deleteImageOpen}
          toggleOpen={toggleDeleteImageOpenState}
          onConfirm={() => {
            onDeleteImage(
              {
                ImagePath: image,
              },
              {
                onSuccess: () => {
                  toast.success(t("hbh.imageDeletedSuccessfully.label"));
                  toggleDeleteImageOpenState();
                },
                onSettled: () => {
                  toggleDeleteImageOpenState();
                },
              }
            );
          }}
          title={t("hbh.deleteImage.label")}
          confirmLabel={t("greco.delete")}
          description={t("greco.dialog.deleteTitle", {
            object: fileName,
          })}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  gap: 5px;
  align-items: flex-end;
  justify-content: space-between;
  padding: 5px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  border-radius: 4px;
  // &:nth-of-type(1) {
  //   border-top: 1px solid ${({ theme }) => theme.palette.neutralLight};
  // }
  &:hover {
    background-color: ${({ theme }) => theme.palette.neutralLighter};
  }
`;
